<template>
    <vx-expandable-card>
        <template v-slot:header>Event Types - Group Mapping</template>
        <template v-slot:headerRight>
            <vx-input
                v-model="calendarSearchQuery"
                placeholder="Search URLs or type"
                size="md"
                type="text"
                :disabled="loadingCalendars"
                class="w-64 font-normal"
            >
                <template v-slot:right>
                    <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                </template>
            </vx-input>
        </template>

        <template v-slot:content>
            <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                Only the team owner can configure integrations.
            </div>
            <div v-else-if="!isConnected" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                Connect your Calendly account first to map calendars to groups.
            </div>
            <div v-else class="flex flex-col gap-6 mt-3">
                <div class="mb-4">
                    Assign Calendly schedules to groups. Contacts will be automatically added to the group when they
                    book a meeting in the assigned schedule.
                </div>

                <!-- Loading calendars -->
                <div v-if="loadingCalendars" class="flex flex-col items-center justify-center gap-4 py-12">
                    <vx-icon-loading spin class="w-8 h-8 text-sky-600"></vx-icon-loading>
                </div>

                <!-- Connection error -->
                <vx-alert v-else-if="connectionError" color="danger" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleExclamation" />
                    </template>
                    <div class="flex flex-col gap-2">
                        <div>Unable to load event types. Please check your Calendly connection.</div>
                    </div>
                </vx-alert>

                <!-- No calendars -->
                <vx-alert v-else-if="allCalendars.length === 0" color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleInfo" />
                    </template>
                    No active event types found in your Calendly account. Create event types in Calendly first, then
                    refresh this page.
                </vx-alert>

                <!-- Calendars list -->
                <template v-else>
                    <div class="flex flex-col gap-4">
                        <!-- No search results -->
                        <vx-alert
                            v-if="displayedCalendars.length === 0 && calendarSearchQuery.trim()"
                            color="info"
                            :closeable="false"
                        >
                            <template v-slot:icon>
                                <font-awesome-icon widthAuto :icon="faCircleInfo" />
                            </template>
                            No event types found matching your search.
                        </vx-alert>

                        <!-- Calendars table -->
                        <vx-table
                            v-if="displayedCalendars.length > 0"
                            class="xl:grid-cols-[2fr_3fr] mt-0"
                            rowBreakpoint="xl"
                        >
                            <vx-table-row isHeader>
                                <div>Calendar Name</div>
                                <div>Assigned Groups</div>
                            </vx-table-row>

                            <vx-table-row v-for="calendar in displayedCalendars" :key="calendar.event_type_uri">
                                <vx-table-row-title>
                                    <div class="flex flex-col gap-1">
                                        <span class="break-all lg:line-clamp-2" :title="calendar.name">{{
                                            calendar.name
                                        }}</span>
                                        <span class="text-xs text-slate-500 flex items-center gap-1">
                                            <font-awesome-icon :icon="faCalendarClock" widthAuto />
                                            {{ calendar.duration }} min
                                        </span>
                                        <span
                                            v-if="calendar.scheduling_url"
                                            class="text-xs text-slate-500 break-all"
                                            :title="calendar.scheduling_url"
                                        >
                                            {{ formatCalendarUrl(calendar.scheduling_url) }}
                                        </span>
                                    </div>
                                </vx-table-row-title>
                                <vx-table-row-item contentClasses="w-full">
                                    <template v-slot:label>Assigned Groups:</template>
                                    <vx-selectable
                                        placeholder="Search for a group..."
                                        :options="groupOptions"
                                        label="name"
                                        :reduce="(group: Group) => group.group_id"
                                        :modelValue="calendarGroupMappings[calendar.event_type_uri]"
                                        @update:modelValue="updateCalendarMapping(calendar.event_type_uri, $event)"
                                        @search="searchGroups"
                                        size="md"
                                        :loading="savingCalendars.includes(calendar.event_type_uri)"
                                        :disabled="savingCalendars.includes(calendar.event_type_uri)"
                                    >
                                        <template v-slot:option="{ option }">
                                            <div class="flex flex-col gap-1 py-1">
                                                <span class="text-sm font-semibold">{{ option.name }}</span>
                                            </div>
                                        </template>
                                        <template v-slot:selected-option="{ option }">
                                            <span class="text-sm">{{ option.name }}</span>
                                        </template>
                                    </vx-selectable>
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>

                        <!-- Load More button -->
                        <div v-if="!calendarSearchQuery && (hasMoreCalendars || nextPageToken)" class="flex mt-4">
                            <vx-button
                                @click="loadMoreCalendars"
                                color="muted-dark"
                                size="lg"
                                :loading="loadingMoreCalendars"
                            >
                                Load More
                            </vx-button>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup lang="ts">
import { inject, ref, computed, reactive, watch } from 'vue';
import {
    VxExpandableCard,
    VxAlert,
    VxTable,
    VxTableRow,
    VxTableRowTitle,
    VxTableRowItem,
    VxButton,
    VxSelectable,
    VxIconLoading,
    VxInput,
} from '@voxie/frontend-components';
import {
    faCircleExclamation,
    faCircleInfo,
    faCalendarClock,
    faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { calendlyClient } from '~/services';
import groupsClient from '~/services/groups/groupsClient';
import type { CalendlyEventType } from '~/services/calendly/calendlyClient.types';
import type { Group } from '~/services/groups/groupsClient.types';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';
import type { Features } from '~/provides/features.types';
import { GROUP_STATUSES_CONFIGURABLE, type GroupStatus } from '~/constants/groupStatuses';

const FEATURES = inject('FEATURES') as Features;

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
    isConnected: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const allCalendars = ref<CalendlyEventType[]>([]);
const loadingCalendars = ref<boolean>(true);
const loadingMoreCalendars = ref<boolean>(false);
const connectionError = ref<boolean>(false);
const calendarsPerPage = 15;
const displayedCalendarsCount = ref<number>(calendarsPerPage);
const calendarSearchQuery = ref<string>('');
const nextPageToken = ref<string | null>(null);

const groupCache = ref<Map<string, Group>>(new Map());

const groupOptions = computed(() => {
    const options: Group[] = [];
    const addedIds = new Set<string>();

    // Get currently selected group IDs
    const selectedGroupIds = Object.values(calendarGroupMappings).filter(Boolean) as string[];

    // Add selected groups first (ensures they're always visible)
    selectedGroupIds.forEach((groupId) => {
        const group = groupCache.value.get(groupId);
        if (group && !addedIds.has(groupId)) {
            options.push(group);
            addedIds.add(groupId);
        }
    });

    // Add all other groups from cache
    groupCache.value.forEach((group) => {
        if (!addedIds.has(group.group_id)) {
            options.push(group);
            addedIds.add(group.group_id);
        }
    });

    return options;
});

// Mappings state (calendar URI -> single group ID)
const calendarGroupMappings = reactive<Record<string, string | undefined>>({});
const savingCalendars = ref<string[]>([]);

const filteredCalendars = computed(() => {
    let filtered = allCalendars.value;

    if (calendarSearchQuery.value.trim()) {
        const query = calendarSearchQuery.value.toLowerCase();
        filtered = filtered.filter(
            (calendar) =>
                calendar.name.toLowerCase().includes(query) || calendar.scheduling_url?.toLowerCase().includes(query)
        );
    }

    return filtered;
});

const displayedCalendars = computed(() => {
    return filteredCalendars.value.slice(0, displayedCalendarsCount.value);
});

const hasMoreCalendars = computed(() => {
    return displayedCalendarsCount.value < filteredCalendars.value.length;
});

watch(calendarSearchQuery, () => {
    displayedCalendarsCount.value = calendarsPerPage;
});

const hasPermission = computed(() => props.ownsTeam);

const formatCalendarUrl = (schedulingUrl: string) => {
    try {
        const url = new URL(schedulingUrl);
        return `${url.hostname}${url.pathname}`;
    } catch {
        return schedulingUrl;
    }
};

const loadAllCalendars = async () => {
    loadingCalendars.value = true;
    connectionError.value = false;
    allCalendars.value = [];
    nextPageToken.value = null;

    try {
        const response = await calendlyClient.getEventTypes({ teamId });

        allCalendars.value = response.data.event_types;
        nextPageToken.value = response.data.pagination?.next_page_token || null;
    } catch (error: unknown) {
        connectionError.value = true;
        toasted.error(catchRequestError(error));
    } finally {
        loadingCalendars.value = false;
    }
};

const searchGroups = debounce(async (search: string, loading?: (isLoading: boolean) => void) => {
    loading?.(true);

    try {
        const params: { per_page: number; query?: string; status?: GroupStatus[] } = {
            per_page: 15,
        };

        if (search) {
            params.query = search;
        }

        if (FEATURES.group_statuses) {
            params.status = GROUP_STATUSES_CONFIGURABLE;
        }

        const response = await groupsClient.getGroups({ teamId }, params);

        // Add groups to cache (accumulate, don't replace)
        response.data.data.forEach((group) => {
            groupCache.value.set(group.group_id, group);
        });

        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        loading?.(false);
    }
}, 300);

const loadInitialGroups = async () => {
    try {
        const params: { per_page: number; status?: GroupStatus[] } = {
            per_page: 15,
        };

        if (FEATURES.group_statuses) {
            params.status = GROUP_STATUSES_CONFIGURABLE;
        }

        const response = await groupsClient.getGroups({ teamId }, params);

        // Add groups to cache
        response.data.data.forEach((group) => {
            groupCache.value.set(group.group_id, group);
        });

        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (error) {
        toasted.error(catchRequestError(error));
    }
};

// Ensure a group is in the cache (for pre-selected groups)
const ensureGroupInOptions = async (groupId: string) => {
    if (groupCache.value.has(groupId)) {
        return; // Already in cache
    }

    try {
        const response = await groupsClient.getGroup({ teamId }, groupId);
        groupCache.value.set(groupId, response.data);
        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (error) {
        toasted.error(catchRequestError(error));
    }
};

const loadMoreCalendars = async () => {
    // If we've displayed all loaded calendars and there's a next page, fetch it
    if (displayedCalendarsCount.value >= allCalendars.value.length && nextPageToken.value) {
        loadingMoreCalendars.value = true;

        try {
            const response = await calendlyClient.getEventTypes({ teamId }, { page_token: nextPageToken.value });

            allCalendars.value = [...allCalendars.value, ...response.data.event_types];
            nextPageToken.value = response.data.pagination?.next_page_token || null;

            // Initialize mappings for new calendars
            const groupIdsToFetch = [];
            for (const calendar of response.data.event_types) {
                if (calendarGroupMappings[calendar.event_type_uri] === undefined) {
                    calendarGroupMappings[calendar.event_type_uri] = calendar.group_id ?? undefined;
                    if (calendar.group_id) {
                        groupIdsToFetch.push(calendar.group_id);
                    }
                }
            }
            await Promise.all(groupIdsToFetch.map((groupId) => ensureGroupInOptions(groupId)));
        } catch (error) {
            toasted.error(catchRequestError(error));
        } finally {
            loadingMoreCalendars.value = false;
        }
    }

    // Show more calendars
    displayedCalendarsCount.value += calendarsPerPage;
};

const initializeCalendarMappings = async () => {
    const groupIdsToFetch = [];

    for (const calendar of allCalendars.value) {
        if (calendarGroupMappings[calendar.event_type_uri] === undefined) {
            // If calendar has a group_id, set it as the initial mapping
            calendarGroupMappings[calendar.event_type_uri] = calendar.group_id ?? undefined;

            // Collect pre-selected groups to fetch in parallel
            if (calendar.group_id) {
                groupIdsToFetch.push(calendar.group_id);
            }
        }
    }

    // Fetch all groups in parallel
    await Promise.all(groupIdsToFetch.map((groupId) => ensureGroupInOptions(groupId)));
};

const updateCalendarMapping = debounce(async (calendarUri: string, selectedGroupId: string | undefined) => {
    if (savingCalendars.value.includes(calendarUri)) {
        return;
    }

    const calendar = allCalendars.value.find((c) => c.event_type_uri === calendarUri);
    if (!calendar) {
        return;
    }

    const previousGroupId = calendarGroupMappings[calendarUri];
    const oldGroupId = calendar.group_id;

    savingCalendars.value.push(calendarUri);

    try {
        // Update local state first
        calendar.group_id = selectedGroupId || null;
        calendarGroupMappings[calendarUri] = selectedGroupId;

        // Collect affected groups (old and new, if different)
        const affectedGroups = new Set<string>();
        if (oldGroupId) {
            affectedGroups.add(oldGroupId);
        }
        if (selectedGroupId) {
            affectedGroups.add(selectedGroupId);
        }

        // For each affected group, rebuild its complete event type list from updated local state
        for (const groupId of affectedGroups) {
            const groupEventTypes = allCalendars.value
                .filter((c) => c.group_id === groupId)
                .map((c) => ({
                    event_type_uri: c.event_type_uri,
                    name: c.name,
                    scheduling_url: c.scheduling_url,
                    duration: c.duration,
                }));

            await calendlyClient.updateGroupMapping({ teamId }, groupId, { event_types: groupEventTypes });
        }

        toasted.success('Calendar mapping updated successfully');
    } catch (error) {
        // Rollback local state on error
        calendar.group_id = oldGroupId || null;
        calendarGroupMappings[calendarUri] = previousGroupId;
        toasted.error(catchRequestError(error));
    } finally {
        savingCalendars.value = savingCalendars.value.filter((uri) => uri !== calendarUri);
    }
}, 500);

watch(
    () => props.isConnected,
    async () => {
        if (props.isConnected && hasPermission.value) {
            await loadAllCalendars();
            await loadInitialGroups();
            await initializeCalendarMappings();
        } else {
            allCalendars.value = [];
            groupCache.value = new Map();
            for (const key in calendarGroupMappings) {
                delete calendarGroupMappings[key];
            }
            displayedCalendarsCount.value = calendarsPerPage;
            calendarSearchQuery.value = '';
            nextPageToken.value = null;
            loadingCalendars.value = false;
            connectionError.value = false;
        }
    },
    {
        immediate: true,
    }
);
</script>
